import type { Tag } from "@hulahoop/ui";
import type { ComponentProps } from "react";
import type {
  BeneficiarySortField,
  BeneficiaryStatus,
  CulturalActivity,
} from "@/domain/beneficiary";
import type { TaxIncentiveTypeValue } from "@/domain/classification/constants";
import type { SignatureRegime } from "@/domain/investor";
import { DEFAULT_EXPORT_FORMAT, type ExportFormat } from "@/domain/export";

type TagTone = NonNullable<ComponentProps<typeof Tag>["variant"]>;

export const ACTIVITY_TONE: Record<CulturalActivity, TagTone> = {
  film: "blue-dark",
  documentary: "blue-dark",
  series: "blue-dark",
  audiovisual_production: "blue-dark",
  theatre: "sage",
  performing_arts: "sage",
  dance: "lime",
  music_festival: "pink",
};

export const INCENTIVE_LABEL: Record<TaxIncentiveTypeValue, string> = {
  film_audiovisual: "promoters.incentive.film_audiovisual",
  performing_arts_music: "promoters.incentive.performing_arts_music",
};

export const INCENTIVE_ARTICLE: Record<TaxIncentiveTypeValue, string> = {
  film_audiovisual: "promoters.quota.article_film",
  performing_arts_music: "promoters.quota.article_music",
};

export const SIGNATURE_REGIME_LABEL: Record<SignatureRegime, string> = {
  sole: "representatives.regime_sole",
  joint: "representatives.regime_joint",
};

export const PROMOTER_STATUSES: readonly BeneficiaryStatus[] = ["active", "inactive", "archived"];

export type PromoterToggleVerb = "restore" | "deactivate" | "activate";

export const promoterToggleVerb = (status: BeneficiaryStatus): PromoterToggleVerb =>
  status === "archived" ? "restore" : status === "active" ? "deactivate" : "activate";

export const promoterToggleTarget = (status: BeneficiaryStatus): BeneficiaryStatus =>
  status === "active" ? "inactive" : "active";

export type PromoterSortKey = "name" | "projects" | "quota" | "created";

export const PROMOTER_SORT_FIELD: Record<PromoterSortKey, BeneficiarySortField> = {
  name: "name",
  projects: "total_projects_count",
  quota: "volume",
  created: "created_at",
};

const PROMOTER_EXPORT_COLUMN: Partial<Record<string, string>> = {
  name: "name",
  vertical: "main_activities",
  email: "email",
  city: "city",
  status: "status",
};

const EXPORT_FORMATS: readonly ExportFormat[] = ["csv", "xlsx", "json"];

export const toExportFormat = (format: string): ExportFormat =>
  EXPORT_FORMATS.find((candidate) => candidate === format) ?? DEFAULT_EXPORT_FORMAT;

export const exportColumnOf = (key: string) => PROMOTER_EXPORT_COLUMN[key];

export const PROMOTER_EXPORT_BASE = ["name", "main_activities"];
